/**
 * Navigation utility functions for ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import type {
  SVGStructure,
  AtomLeaf,
  BondLeaf,
  BondEndpointLeaf,
  LonePairLeaf,
  FeatureNode,
  MoleculeNode,
} from './types';

/**
 * Tree navigation data structure for hierarchical keyboard navigation
 */
export interface TreeNavigation {
  /** Map from node ID to parent ID (null for root molecules) */
  parentOf: Map<string, string | null>;
  /** Map from node ID to ordered list of child IDs */
  childrenOf: Map<string, string[]>;
  /** Map from node ID to index among siblings */
  siblingIndex: Map<string, number>;
  /** All root molecule IDs */
  rootIds: string[];
}

/**
 * Helper to add atom and its children (hydrogens, lone pairs, bonds, endpoints) to IDs list
 */
function addAtomWithChildren(atom: AtomLeaf, ids: string[]): void {
  ids.push(atom.id);
  // Add hydrogen children first
  for (const h of atom.hydrogenNodes) {
    ids.push(h.id);
    // Add hydrogen's lone pairs
    for (const lp of h.lonePairNodes) {
      ids.push(lp.id);
    }
  }
  // Then add lone pairs
  for (const lp of atom.lonePairNodes) {
    ids.push(lp.id);
  }
  // Then add bonds and their endpoint children
  for (const bond of atom.bondNodes) {
    ids.push(bond.id);
    // Add bond endpoints
    for (const endpoint of bond.endpointNodes) {
      ids.push(endpoint.id);
    }
  }
}

/**
 * Get all navigable node IDs in tree order (depth-first)
 */
export function getTreeNodeIds(structure: SVGStructure): string[] {
  const ids: string[] = [];

  for (const molecule of structure.tree.molecules) {
    ids.push(molecule.id);

    // Features (recursive)
    const addFeature = (feature: FeatureNode) => {
      ids.push(feature.id);
      // Bonds are now children of atoms, not features
      for (const atom of feature.atoms) {
        addAtomWithChildren(atom, ids);
      }
      for (const child of feature.children) {
        addFeature(child);
      }
    };

    for (const feature of molecule.features) {
      addFeature(feature);
    }

    // Unclaimed atoms (bonds are now children of atoms)
    for (const atom of molecule.unclaimedAtoms) {
      addAtomWithChildren(atom, ids);
    }
  }

  return ids;
}

/**
 * Get the node for a given ID
 */
export function getNodeById(
  structure: SVGStructure,
  id: string
): AtomLeaf | BondLeaf | BondEndpointLeaf | LonePairLeaf | FeatureNode | MoleculeNode | undefined {
  // Check index first
  if (structure.index.atomsById.has(id)) {
    return structure.index.atomsById.get(id);
  }
  if (structure.index.bondsById.has(id)) {
    return structure.index.bondsById.get(id);
  }
  if (structure.index.endpointsById.has(id)) {
    return structure.index.endpointsById.get(id);
  }
  if (structure.index.lonePairsById.has(id)) {
    return structure.index.lonePairsById.get(id);
  }
  if (structure.index.moleculesById.has(id)) {
    return structure.index.moleculesById.get(id);
  }
  if (structure.index.featuresById.has(id)) {
    return structure.index.featuresById.get(id);
  }
  return undefined;
}

/**
 * Build navigation data structure for hierarchical tree traversal
 */
export function buildTreeNavigation(structure: SVGStructure): TreeNavigation {
  const parentOf = new Map<string, string | null>();
  const childrenOf = new Map<string, string[]>();
  const siblingIndex = new Map<string, number>();
  const rootIds: string[] = [];

  // Helper to register children
  const registerChildren = (parentId: string | null, childIds: string[]) => {
    if (parentId !== null) {
      childrenOf.set(parentId, childIds);
    }
    childIds.forEach((childId, index) => {
      parentOf.set(childId, parentId);
      siblingIndex.set(childId, index);
    });
  };

  // Process molecules (root level)
  const moleculeIds = structure.tree.molecules.map(m => m.id);
  rootIds.push(...moleculeIds);
  registerChildren(null, moleculeIds);

  // Helper to process atom and its children
  const processAtom = (atom: AtomLeaf) => {
    // Atom's children: hydrogens, lone pairs, then bonds
    const atomChildren: string[] = [];
    for (const h of atom.hydrogenNodes) {
      atomChildren.push(h.id);
    }
    for (const lp of atom.lonePairNodes) {
      atomChildren.push(lp.id);
    }
    for (const bond of atom.bondNodes) {
      atomChildren.push(bond.id);
    }
    registerChildren(atom.id, atomChildren);

    // Process hydrogen children (they can have their own lone pairs)
    for (const h of atom.hydrogenNodes) {
      const hChildren = h.lonePairNodes.map(lp => lp.id);
      registerChildren(h.id, hChildren);
      for (const lp of h.lonePairNodes) {
        childrenOf.set(lp.id, []);
      }
    }

    // Lone pairs have no children
    for (const lp of atom.lonePairNodes) {
      childrenOf.set(lp.id, []);
    }

    // Bonds have endpoint children
    for (const bond of atom.bondNodes) {
      const endpointIds = bond.endpointNodes.map(ep => ep.id);
      registerChildren(bond.id, endpointIds);
      // Endpoints are leaf nodes (no children)
      for (const endpoint of bond.endpointNodes) {
        childrenOf.set(endpoint.id, []);
      }
    }
  };

  for (const molecule of structure.tree.molecules) {
    // Molecule's children: features, then unclaimed atoms
    const moleculeChildren: string[] = [];

    // Add features
    for (const feature of molecule.features) {
      moleculeChildren.push(feature.id);
    }

    // Add unclaimed atoms (bonds are now children of atoms)
    for (const atom of molecule.unclaimedAtoms) {
      moleculeChildren.push(atom.id);
    }

    registerChildren(molecule.id, moleculeChildren);

    // Process features
    for (const feature of molecule.features) {
      // Feature's children: atoms only (bonds are now children of atoms)
      const featureChildren: string[] = [];

      for (const atom of feature.atoms) {
        featureChildren.push(atom.id);
      }

      registerChildren(feature.id, featureChildren);

      // Process atoms in feature
      for (const atom of feature.atoms) {
        processAtom(atom);
      }
    }

    // Process unclaimed atoms
    for (const atom of molecule.unclaimedAtoms) {
      processAtom(atom);
    }
  }

  return { parentOf, childrenOf, siblingIndex, rootIds };
}

/**
 * Navigation helper functions
 */
export const treeNavHelpers = {
  hasChildren: (nav: TreeNavigation, id: string): boolean => {
    const children = nav.childrenOf.get(id);
    return children !== undefined && children.length > 0;
  },

  getParent: (nav: TreeNavigation, id: string): string | null => {
    return nav.parentOf.get(id) ?? null;
  },

  getChildren: (nav: TreeNavigation, id: string): string[] => {
    return nav.childrenOf.get(id) ?? [];
  },

  getFirstChild: (nav: TreeNavigation, id: string): string | null => {
    const children = nav.childrenOf.get(id);
    return children && children.length > 0 ? children[0] : null;
  },

  getLastChild: (nav: TreeNavigation, id: string): string | null => {
    const children = nav.childrenOf.get(id);
    return children && children.length > 0 ? children[children.length - 1] : null;
  },

  getSiblings: (nav: TreeNavigation, id: string): string[] => {
    const parentId = nav.parentOf.get(id);
    if (parentId === null) {
      return nav.rootIds;
    }
    if (parentId === undefined) {
      return [];
    }
    return nav.childrenOf.get(parentId) ?? [];
  },

  getNextSibling: (nav: TreeNavigation, id: string): string | null => {
    const siblings = treeNavHelpers.getSiblings(nav, id);
    const index = nav.siblingIndex.get(id) ?? -1;
    if (index >= 0 && index < siblings.length - 1) {
      return siblings[index + 1];
    }
    return null;
  },

  getPrevSibling: (nav: TreeNavigation, id: string): string | null => {
    const siblings = treeNavHelpers.getSiblings(nav, id);
    const index = nav.siblingIndex.get(id) ?? -1;
    if (index > 0) {
      return siblings[index - 1];
    }
    return null;
  },

  /**
   * Get next visible node in tree order (respecting expanded state)
   * ArrowDown behavior: if expanded with children, go to first child; else go to next sibling or ancestor's next sibling
   */
  getNextVisible: (nav: TreeNavigation, id: string, expandedIds: Set<string>): string | null => {
    // If expanded and has children, go to first child
    if (expandedIds.has(id) && treeNavHelpers.hasChildren(nav, id)) {
      return treeNavHelpers.getFirstChild(nav, id);
    }

    // Otherwise, try next sibling
    let current: string | null = id;
    while (current !== null) {
      const nextSibling = treeNavHelpers.getNextSibling(nav, current);
      if (nextSibling !== null) {
        return nextSibling;
      }
      // Go up to parent and try its next sibling
      current = treeNavHelpers.getParent(nav, current);
    }

    return null;
  },

  /**
   * Get previous visible node in tree order (respecting expanded state)
   * ArrowUp behavior: go to previous sibling's last visible descendant, or parent
   */
  getPrevVisible: (nav: TreeNavigation, id: string, expandedIds: Set<string>): string | null => {
    const prevSibling = treeNavHelpers.getPrevSibling(nav, id);

    if (prevSibling !== null) {
      // Go to previous sibling's last visible descendant
      return treeNavHelpers.getLastVisibleDescendant(nav, prevSibling, expandedIds);
    }

    // No previous sibling, go to parent
    return treeNavHelpers.getParent(nav, id);
  },

  /**
   * Get the last visible descendant of a node (for ArrowUp navigation)
   */
  getLastVisibleDescendant: (nav: TreeNavigation, id: string, expandedIds: Set<string>): string => {
    // If not expanded or no children, return the node itself
    if (!expandedIds.has(id) || !treeNavHelpers.hasChildren(nav, id)) {
      return id;
    }

    // Go to last child and recurse
    const lastChild = treeNavHelpers.getLastChild(nav, id);
    if (lastChild === null) {
      return id;
    }

    return treeNavHelpers.getLastVisibleDescendant(nav, lastChild, expandedIds);
  },
};

/**
 * Helper to collect all descendant IDs from an atom (including hydrogens, lone pairs, bonds, endpoints)
 */
function getAtomDescendantIds(atom: AtomLeaf): string[] {
  const ids: string[] = [atom.atomId];

  // Add hydrogen children and their lone pairs
  for (const h of atom.hydrogenNodes) {
    ids.push(h.atomId);
    for (const lp of h.lonePairNodes) {
      ids.push(lp.lonePairId);
    }
  }

  // Add lone pairs
  for (const lp of atom.lonePairNodes) {
    ids.push(lp.lonePairId);
  }

  // Add bonds and their endpoints
  for (const bond of atom.bondNodes) {
    ids.push(bond.bondId);
    // Add endpoint IDs
    for (const endpoint of bond.endpointNodes) {
      ids.push(endpoint.id);
    }
  }

  return ids;
}

/**
 * Helper to collect all descendant IDs from a feature
 */
function getFeatureDescendantIds(feature: FeatureNode): string[] {
  const ids: string[] = [];

  // Add atoms and their descendants (hydrogens, lone pairs, and bonds)
  // Bonds are now children of atoms, not features
  for (const atom of feature.atoms) {
    ids.push(...getAtomDescendantIds(atom));
  }

  // Recursively add nested features
  for (const child of feature.children) {
    ids.push(...getFeatureDescendantIds(child));
  }

  return ids;
}

/**
 * Get all descendant element IDs for a molecule, feature, or single element.
 *
 * When focused on a molecule or feature in the NavigableTree, this function
 * returns all atom, bond, lone pair, and bond endpoint IDs that belong to it.
 * For individual elements, returns just that element's ID.
 *
 * @param nodeId - The ID of the node to get descendants for
 * @param structure - The SVG structure containing the tree
 * @returns Array of all descendant element IDs (atoms, bonds, lone pairs, endpoints)
 */
export function getDescendantIds(nodeId: string, structure: SVGStructure): string[] {
  // Check if it's a molecule
  const molecule = structure.index.moleculesById.get(nodeId);
  if (molecule) {
    const ids: string[] = [];

    // Add all features and their descendants
    for (const feature of molecule.features) {
      ids.push(...getFeatureDescendantIds(feature));
    }

    // Add unclaimed atoms and their descendants (hydrogens, lone pairs, and bonds)
    // Bonds are now children of atoms, not molecules
    for (const atom of molecule.unclaimedAtoms) {
      ids.push(...getAtomDescendantIds(atom));
    }

    return ids;
  }

  // Check if it's a feature
  const feature = structure.index.featuresById.get(nodeId);
  if (feature) {
    return getFeatureDescendantIds(feature);
  }

  // Check if it's an atom (may have feature-scoped ID)
  const atom = structure.index.atomsById.get(nodeId);
  if (atom) {
    return getAtomDescendantIds(atom);
  }

  // Check if it's a lone pair (may have feature-scoped ID)
  const lonePair = structure.index.lonePairsById.get(nodeId);
  if (lonePair) {
    // Return the original atom's lone pair ID for SVG reference
    return [lonePair.atomId + '-lp' + lonePair.index];
  }

  // Single element (bond, bond endpoint) - extract original ID if scoped
  // For tree IDs like "feature-1:atom-1", extract "atom-1"
  if (nodeId.includes(':')) {
    return [nodeId.split(':')[1]];
  }
  return [nodeId];
}
